// =============================================================================
// Phase 8: Expression Resolver
// Resolves parsed expressions to actual Date objects
// =============================================================================

import { parseExpression } from "./expressionParser";

// Weekday name to day number (0=Sunday, 1=Monday, etc.)
const WEEKDAY_NUMS: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export interface Operation {
  type: "subtract" | "add" | "round";
  value?: number;
  unit: string;
}

export interface ParsedExpression {
  anchor: string;
  operations: Operation[];
}

export interface ResolveOptions {
  timezone?: string;
  now?: Date;
  baseDate?: Date; // For start/end anchors - the reference date from the other field
}

/**
 * Check if anchor is an ISO date (YYYY-MM-DD)
 */
function isISODateAnchor(anchor: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(anchor);
}

/**
 * Parse ISO date anchor to Date object
 */
function parseISODateAnchor(anchor: string): Date {
  const [year, month, day] = anchor.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

/**
 * Resolve a parsed expression to a Date
 */
export function resolve(
  expression: ParsedExpression,
  options: ResolveOptions = {}
): Date {
  const { timezone = "UTC", now, baseDate } = options;

  // Start with anchor time
  let result: Date;
  if (expression.anchor && isISODateAnchor(expression.anchor)) {
    // ISO date anchor (e.g., "2026-01-15")
    result = parseISODateAnchor(expression.anchor);
  } else if (expression.anchor === "start" || expression.anchor === "end") {
    // start/end anchor - use the provided baseDate
    if (!baseDate) {
      const otherField = expression.anchor === "start" ? "start" : "end";
      throw new Error(`Enter a valid date or expression in the ${otherField} field first`);
    }
    result = new Date(baseDate);
  } else {
    // "now" anchor or default
    result = now ? new Date(now) : new Date();
  }

  // Apply each operation in sequence
  for (const op of expression.operations) {
    result = applyOperation(result, op, timezone);
  }

  return result;
}

/**
 * Apply a single operation to a date
 */
function applyOperation(date: Date, op: Operation, timezone: string): Date {
  switch (op.type) {
    case "subtract":
      return applyAddSubtract(date, -(op.value ?? 1), op.unit);
    case "add":
      return applyAddSubtract(date, op.value ?? 1, op.unit);
    case "round":
      return applyRound(date, op.unit, timezone);
    default:
      return date;
  }
}

/**
 * Apply add/subtract operation
 */
function applyAddSubtract(date: Date, value: number, unit: string): Date {
  const result = new Date(date);

  switch (unit) {
    case "s": // seconds
      result.setUTCSeconds(result.getUTCSeconds() + value);
      break;
    case "m": // minutes
      result.setUTCMinutes(result.getUTCMinutes() + value);
      break;
    case "h": // hours
      result.setUTCHours(result.getUTCHours() + value);
      break;
    case "d": // days
      result.setUTCDate(result.getUTCDate() + value);
      break;
    case "w": // weeks
      result.setUTCDate(result.getUTCDate() + value * 7);
      break;
    case "M": // months
      result.setUTCMonth(result.getUTCMonth() + value);
      break;
    case "y": // years
      result.setUTCFullYear(result.getUTCFullYear() + value);
      break;
  }

  return result;
}

/**
 * Apply rounding operation
 */
function applyRound(date: Date, unit: string, timezone: string): Date {
  // For UTC timezone, use simple UTC-based rounding
  if (timezone === "UTC") {
    return roundUTC(date, unit);
  }

  // For other timezones, we still return a valid Date
  // (full timezone support would require a library like date-fns-tz or luxon)
  return roundUTC(date, unit);
}

/**
 * Round date in UTC
 */
function roundUTC(date: Date, unit: string): Date {
  const result = new Date(date);

  switch (unit) {
    case "s": // round to start of second
      result.setUTCMilliseconds(0);
      break;

    case "m": // round to start of minute
      result.setUTCSeconds(0, 0);
      break;

    case "h": // round to start of hour
      result.setUTCMinutes(0, 0, 0);
      break;

    case "d": // round to start of day
      result.setUTCHours(0, 0, 0, 0);
      break;

    case "w": // round to start of week (Monday)
      result.setUTCHours(0, 0, 0, 0);
      // getUTCDay: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      // We want Monday (1) as start of week
      const dayOfWeek = result.getUTCDay();
      // Calculate days to subtract to get to Monday
      // If Sunday (0), go back 6 days
      // If Monday (1), go back 0 days
      // If Tuesday (2), go back 1 day
      // etc.
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      result.setUTCDate(result.getUTCDate() - daysToSubtract);
      break;

    case "M": // round to start of month
      result.setUTCDate(1);
      result.setUTCHours(0, 0, 0, 0);
      break;

    case "y": // round to start of year
      result.setUTCMonth(0, 1);
      result.setUTCHours(0, 0, 0, 0);
      break;
  }

  return result;
}

// =============================================================================
// Natural Language & Keyword Resolution
// =============================================================================

export interface NaturalLanguageResult {
  type: string;
  base?: string;
  hour?: number;
  minute?: number;
}

/**
 * Resolve natural language result to Date
 */
export function resolveNaturalLanguage(
  result: NaturalLanguageResult,
  fieldType?: string,
  granularity?: string
): Date {
  const now = new Date();

  if (result.type === "keyword" || result.type === "relative") {
    const base = result.base || "today";
    const date = new Date(now);

    if (base === "yesterday") {
      date.setUTCDate(date.getUTCDate() - 1);
    } else if (base === "tomorrow") {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    // Apply time based on fieldType
    if (fieldType === "to") {
      date.setUTCHours(23, 59, 59, 999);
    } else {
      date.setUTCHours(0, 0, 0, 0);
    }

    return date;
  }

  if (result.type === "time") {
    const date = new Date(now);
    date.setUTCHours(result.hour || 0, result.minute || 0, 0, 0);
    return date;
  }

  return now;
}

/**
 * Resolve weekday to next occurrence of that day
 */
export function resolveWeekday(day: string, fieldType?: string): Date {
  const now = new Date();
  const targetDay = WEEKDAY_NUMS[day.toLowerCase()];
  const currentDay = now.getUTCDay();

  // Calculate days until target (if today is target, use today)
  let daysUntil = targetDay - currentDay;
  if (daysUntil < 0) daysUntil += 7;

  const date = new Date(now);
  date.setUTCDate(date.getUTCDate() + daysUntil);

  // Apply time based on fieldType
  if (fieldType === "to") {
    date.setUTCHours(23, 59, 59, 999);
  } else {
    date.setUTCHours(0, 0, 0, 0);
  }

  return date;
}

/**
 * Resolve relative weekday (last monday, next friday)
 */
export function resolveRelativeWeekday(day: string, direction: string, fieldType?: string): Date {
  const now = new Date();
  const targetDay = WEEKDAY_NUMS[day.toLowerCase()];
  const currentDay = now.getUTCDay();

  const date = new Date(now);

  if (direction === "last" || direction === "previous") {
    // Go to last occurrence of this day
    let daysBack = currentDay - targetDay;
    if (daysBack <= 0) daysBack += 7;
    date.setUTCDate(date.getUTCDate() - daysBack);
  } else {
    // Go to next occurrence of this day
    let daysForward = targetDay - currentDay;
    if (daysForward <= 0) daysForward += 7;
    date.setUTCDate(date.getUTCDate() + daysForward);
  }

  // Apply time based on fieldType
  if (fieldType === "to") {
    date.setUTCHours(23, 59, 59, 999);
  } else {
    date.setUTCHours(0, 0, 0, 0);
  }

  return date;
}

export interface DateComponents {
  year?: number;
  month?: number;
  day?: number;
  hour?: number;
  minute?: number;
  second?: number;
  millisecond?: number;
}

/**
 * Build Date from parsed date components
 */
export function buildDate(
  dateResult: DateComponents,
  fieldType?: string,
  granularity?: string
): Date {
  const now = new Date();
  const year = dateResult.year ?? now.getUTCFullYear();
  const month = (dateResult.month ?? 1) - 1; // JS months are 0-indexed
  const day = dateResult.day ?? 1;

  let hour = dateResult.hour;
  let minute = dateResult.minute;
  let second = dateResult.second ?? 0;
  let ms = dateResult.millisecond ?? 0;

  // If no time specified, apply granularity-based defaults
  if (hour === undefined && minute === undefined) {
    if (fieldType === "to") {
      if (granularity === "hour") {
        hour = 23;
        minute = 0;
        second = 0;
        ms = 0;
      } else if (granularity === "second") {
        hour = 23;
        minute = 59;
        second = 59;
        ms = 0;
      } else {
        // minute granularity or default
        hour = 23;
        minute = 59;
        second = 59;
        ms = 999;
      }
    } else {
      hour = 0;
      minute = 0;
      second = 0;
      ms = 0;
    }
  } else {
    hour = hour ?? 0;
    minute = minute ?? 0;
  }

  return new Date(Date.UTC(year, month, day, hour, minute, second, ms));
}

/**
 * Resolve an expression string like "now-7d" or "now/w"
 */
export function resolveExpressionString(expr: string, timezone?: string): Date {
  // Handle "today 00:00" style expressions
  if (expr.startsWith("today") || expr.startsWith("yesterday") || expr.startsWith("tomorrow")) {
    const parts = expr.split(" ");
    const base = parts[0];
    const time = parts[1];

    const now = new Date();
    const date = new Date(now);

    if (base === "yesterday") {
      date.setUTCDate(date.getUTCDate() - 1);
    } else if (base === "tomorrow") {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    if (time) {
      const [h, m] = time.split(":").map(Number);
      date.setUTCHours(h, m, m === 59 ? 59 : 0, m === 59 ? 999 : 0);
    } else {
      date.setUTCHours(0, 0, 0, 0);
    }

    return date;
  }

  // Parse as expression
  const exprResult = parseExpression(expr);
  if (exprResult.isExpression) {
    return resolve({ anchor: exprResult.anchor || "now", operations: exprResult.operations }, { timezone });
  }

  return new Date();
}
